import { useCallback, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Column,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Hyperlink,
  Input,
  Separator,
  useToast,
} from "@racoon/ui";
import { Namespace, TranslationKey, type TFn, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { GripVertical, MoreHorizontal, Pencil, Search } from "lucide-react";
import { IcCancel, IcExcel, IcGroupOfLocations, IcRoutes } from "@racoon/icons";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useCurrentUser } from "~/shared/hooks/useCurrentUser";
import {
  GroupStops,
  useAssignRouteModal,
} from "~/features/assign-route/hooks/use-assign-route-modal";
import {
  useLazyGetPickupLocationGroupByIdQuery,
  useLazyPreviewPickupLocationGroupJobsQuery,
  useReorderContainerGroupsInPickupLocationGroupMutation,
  useUnbindContainerGroupFromPickupLocationGroupMutation,
} from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import {
  buildGroupDraftJobs,
  computeGroupStats,
  containerGroupTotalVolume,
  indexContainerGroupsByLocation,
  linkJobTypeName,
  orderGroupContainerGroups,
} from "../model/helpers";
import { containerGroupRequiredJobTypes } from "~/shared/lib/container-group-job-types";
import type { ServiceDayFilter } from "../model/use-service-day-filter";
import { useContainerGroupSchedules } from "~/shared/api/container-group-schedule/use-location-schedules";
import { exportToExcel, generateFilename } from "~/shared/lib/export-utils";
import { BindLocationsModal } from "./bind-locations.modal";
import { ServiceDayFilterControls } from "./service-day-filter";
import { EditGroupModal } from "./edit-group.modal";
import { usePickupLocationCard } from "~/features/pickup-location-modal/hooks/use-pickup-location-card";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";

import { EMPTY_CELL, normalizeForSearch, startOfLocalDay, toTimestamp } from "@racoon/lib";
type Props = {
  group: ConnectProto.PickupLocationGroup;
  locations: ConnectProto.PickupLocation[];
  trucks: ConnectProto.Truck[];
  loading?: boolean;
  /** Single-day view, owned by the page so the map narrows with the table. */
  serviceDay: ServiceDayFilter;
  /** Optimistic order after a drag, owned by the page so the map matches the table. */
  localOrder: string[] | null;
  onLocalOrderChange: (order: string[] | null) => void;
  onDeleted?: () => void;
};

// Excel refuses sheet names longer than 31 chars or containing []:*?/\.
const toSheetName = (name: string) => name.replace(/[[\]:*?/\\]/g, " ").slice(0, 31) || "Sheet1";

type BoundRow = {
  index: number;
  cg: ConnectProto.ContainerGroup;
  locationId: string;
  locationName: string;
  customerName: string;
  customer?: ConnectProto.Organization;
  jobTypes: string[];
  /** Whether the container group has a service schedule at all; undefined: unknown. */
  hasSchedule?: boolean;
  volume: number;
};

const scheduleLabel = (t: TFn, hasSchedule?: boolean) =>
  hasSchedule === undefined ? EMPTY_CELL : t(Namespace.Table, hasSchedule ? "yes" : "no");

export const GroupDetailPanel = ({
  group,
  locations,
  trucks,
  loading,
  serviceDay,
  localOrder,
  onLocalOrderChange,
  onDeleted,
}: Props) => {
  const t = useT();
  const { toast } = useToast();
  const { selectedCompanyId } = useSelectedCompany();
  const currentUser = useCurrentUser();
  const { openAssignRoute } = useAssignRouteModal();

  const [search, setSearch] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);
  const [unbindingCgId, setUnbindingCgId] = useState<string | null>(null);
  const [unbindContainerGroup] = useUnbindContainerGroupFromPickupLocationGroupMutation();
  const [reorderContainerGroups] = useReorderContainerGroupsInPickupLocationGroupMutation();

  // Locations list carries richer container-group data (owner organization,
  // location name) than the group response, so index both by container group id.
  const { locationByCgId, richCgById } = useMemo(
    () => indexContainerGroupsByLocation(locations),
    [locations],
  );

  // A site or a customer opens as a card over the group: the group is the
  // context the dispatcher is working in, and the registries would lose it.
  const { openLocation, openLocationById, pickupLocationCard } = usePickupLocationCard();
  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: selectedCompanyId ?? undefined,
  });

  // The bound locations come with the list the panel already holds; a site
  // outside it - the list is scoped to the company - is fetched on click.
  const openLocationCard = useCallback(
    (row: BoundRow) => {
      const known = locationByCgId.get(String(row.cg.id));
      if (known) {
        openLocation(known);
        return;
      }
      openLocationById(row.locationId);
    },
    [locationByCgId, openLocation, openLocationById],
  );

  const {
    schedulesByCgId,
    loaded: schedulesLoaded,
    unknownCgIds,
  } = useContainerGroupSchedules(group.containerGroups);

  // Job types of the actual (group, container group, job type) bindings.
  const linkJobTypesByCgId = useMemo(() => {
    const map = new Map<string, string[]>();
    (group.containerGroupLinks ?? []).forEach((link) => {
      const id = String(link.containerGroupId);
      map.set(id, [...(map.get(id) ?? []), linkJobTypeName(link)]);
    });
    return map;
  }, [group.containerGroupLinks]);

  // With the day filter on, only the container groups due that day; their job
  // types are the ones due, not every bound one.
  const dueByCgId = serviceDay.dueJobTypesByCgId;
  const dayContainerGroups = useMemo(
    () => orderGroupContainerGroups(group, localOrder, dueByCgId),
    [group, localOrder, dueByCgId],
  );

  const rows = useMemo((): BoundRow[] => {
    const mapped = dayContainerGroups.map((cg, i) => {
      const rich = richCgById.get(String(cg.id));
      const location = locationByCgId.get(String(cg.id));
      // The locations response carries richer data (contract, owner org).
      const effectiveCg = rich ?? cg;
      const linkJobTypes = linkJobTypesByCgId.get(String(cg.id)) ?? [];
      const schedules = schedulesByCgId.get(String(cg.id));
      return {
        index: i + 1,
        cg: effectiveCg,
        locationId: String(cg.pickupLocationId),
        locationName: cg.pickupLocationName || location?.name || `#${cg.pickupLocationId}`,
        customerName: rich?.ownerOrganization?.name ?? cg.ownerOrganization?.name ?? "—",
        customer: rich?.ownerOrganization ?? cg.ownerOrganization,
        jobTypes:
          dueByCgId?.get(String(cg.id)) ??
          (linkJobTypes.length > 0
            ? linkJobTypes
            : containerGroupRequiredJobTypes(effectiveCg, schedules)),
        // Not loaded yet, or its chunk failed: unknown, never "no".
        hasSchedule:
          schedulesLoaded && !unknownCgIds.has(String(cg.id)) ? !!schedules?.length : undefined,
        volume: containerGroupTotalVolume(effectiveCg),
      };
    });
    const q = normalizeForSearch(search);
    if (!q) return mapped;
    return mapped.filter(
      (row) =>
        normalizeForSearch(row.locationName).includes(q) ||
        normalizeForSearch(row.customerName).includes(q),
    );
  }, [
    dayContainerGroups,
    richCgById,
    locationByCgId,
    linkJobTypesByCgId,
    schedulesByCgId,
    schedulesLoaded,
    unknownCgIds,
    dueByCgId,
    search,
  ]);

  const stats = useMemo(() => computeGroupStats(dayContainerGroups), [dayContainerGroups]);

  const assignedTruck = useMemo(
    () => trucks.find((truck) => group.defaultTruckId && truck.id === group.defaultTruckId) ?? null,
    [trucks, group.defaultTruckId],
  );

  // An empty job type tells the backend to remove ALL of the container
  // group's links in this group, whatever job types they were created with.
  const handleUnbind = useCallback(
    async (cg: ConnectProto.ContainerGroup) => {
      setUnbindingCgId(String(cg.id));
      try {
        // job_type is required in the proto; UNDEFINED tells the backend to drop
        // every link of the container group, i.e. the whole row.
        await unbindContainerGroup({
          pickupLocationGroupId: group.id,
          containerGroupId: cg.id,
          jobType: ConnectProto.JobType.UNDEFINED,
        }).unwrap();
        toast({ title: t(Namespace.PickupLocation, "groups.notifications.unbound") });
      } catch {
        toast({
          variant: "destructive",
          title: t(Namespace.PickupLocation, "groups.notifications.error"),
          description: t(Namespace.PickupLocation, "groups.notifications.errorDescription"),
        });
      } finally {
        setUnbindingCgId(null);
      }
    },
    [group.id, unbindContainerGroup, toast, t],
  );

  // Persist the new order; rows are keyed by container group id.
  const handleRowDragEnd = useCallback(
    (reordered: BoundRow[]) => {
      const orderedCgIds = reordered.map((row) => String(row.cg.id));
      onLocalOrderChange(orderedCgIds);
      reorderContainerGroups({
        pickupLocationGroupId: group.id,
        containerGroupIds: reordered.map((row) => row.cg.id),
      })
        .unwrap()
        .catch(() => {
          onLocalOrderChange(null);
          toast({
            variant: "destructive",
            title: t(Namespace.PickupLocation, "groups.notifications.error"),
            description: t(Namespace.PickupLocation, "groups.notifications.errorDescription"),
          });
        });
    },
    [group.id, reorderContainerGroups, onLocalOrderChange, toast, t],
  );

  // On a single day only the links due then; gates the "create route" button.
  const routeLinks = useMemo(
    () =>
      (group.containerGroupLinks ?? []).filter(
        (link) =>
          !dueByCgId ||
          dueByCgId.get(String(link.containerGroupId))?.includes(linkJobTypeName(link)),
      ),
    [group.containerGroupLinks, dueByCgId],
  );

  const [previewGroupJobs, { isFetching: isPreviewingRoute }] =
    useLazyPreviewPickupLocationGroupJobsQuery();
  const [getGroup, { isFetching: isLoadingTemplate }] = useLazyGetPickupLocationGroupByIdQuery();

  const draftsOf = useCallback(
    (
      plg: ConnectProto.PickupLocationGroup,
      stops: ConnectProto.JobStop[],
      taken?: ConnectProto.JobStop[],
    ) =>
      buildGroupDraftJobs({
        group: plg,
        stops,
        taken,
        locations,
        serviceProviderId: selectedCompanyId ? BigInt(selectedCompanyId) : undefined,
        authorId: currentUser?.id != null ? BigInt(currentUser.id) : undefined,
      }),
    [locations, selectedCompanyId, currentUser],
  );

  // The route's stops come from the backend's preview of the day: a stop that
  // already has a free job comes as that job (bulk create reuses it), an empty
  // window as a draft, and a taken window as a draft flagged as the duplicate
  // it will be. Nothing is written until the dispatcher submits the
  // assign-route modal, which calls this again when its date changes.
  const loadGroupStops = useCallback(
    async (date: Date): Promise<GroupStops> => {
      const { stops, taken } = await previewGroupJobs({
        pickupLocationGroupId: group.id,
        date: toTimestamp(startOfLocalDay(date)),
        ownerOrganizationId: BigInt(selectedCompanyId ?? 0),
      }).unwrap();

      return {
        jobs: stops.flatMap((stop) => (stop.job ? [stop.job] : [])),
        drafts: draftsOf(
          group,
          stops.filter((stop) => stop.containerGroupId && !stop.job),
          // A taken job_id stop names a job bulk create refuses even with
          // allowDuplicates; only taken windows can be duplicated.
          taken.filter((stop) => stop.containerGroupId),
        ),
      };
    },
    [previewGroupJobs, group, selectedCompanyId, draftsOf],
  );

  // The template mode skips the preview: a stop per link of the group as it is
  // stored now. Bulk create resolves each one against the day: it reuses the
  // window's own unrouted job and creates a job otherwise. Without a window
  // picked, it takes each container group's first window of the day, or the
  // whole day when it has none.
  const loadTemplateStops = useCallback(async (): Promise<GroupStops> => {
    const plg = await getGroup({
      id: group.id,
      ownerOrganizationId: BigInt(selectedCompanyId ?? 0),
    }).unwrap();
    const stops = (plg.containerGroupLinks ?? []).map(
      (link) =>
        ({
          containerGroupId: link.containerGroupId,
          jobType: link.jobType,
        }) as ConnectProto.JobStop,
    );
    return { jobs: [], drafts: draftsOf(plg, stops) };
  }, [getGroup, group.id, selectedCompanyId, draftsOf]);

  // On a single day the route is that day's, otherwise today's.
  const handleCreateRouteFromGroup = useCallback(
    async (template: boolean) => {
      if (!selectedCompanyId) return;
      const date = dueByCgId ? serviceDay.date : new Date();
      try {
        const { jobs, drafts } = template ? await loadTemplateStops() : await loadGroupStops(date);
        openAssignRoute({
          pickupLocationGroupId: group.id,
          groupTemplate: template,
          // The template's jobs do not depend on the day.
          loadGroupStops: template ? undefined : loadGroupStops,
          preselectedTruckId:
            group.defaultTruckId && group.defaultTruckId !== 0n
              ? String(group.defaultTruckId)
              : undefined,
          preselectedJobs: jobs,
          draftJobs: drafts,
          initialDate: date,
        });
      } catch {
        // Reported by GlobalErrorToaster.
      }
    },
    [
      openAssignRoute,
      loadGroupStops,
      loadTemplateStops,
      group.id,
      group.defaultTruckId,
      selectedCompanyId,
      dueByCgId,
      serviceDay.date,
    ],
  );

  const hasBoundContainerGroups = routeLinks.length > 0;

  const columns = useMemo(
    (): Column<BoundRow>[] => [
      {
        id: "index",
        header: t(Namespace.PickupLocation, "groups.table.number"),
        key: "index",
        width: 48,
        align: "center",
        sortable: true,
      },
      // No handle on a single day: the reorder request writes positions for
      // exactly the ids it gets, so a filtered subset would interleave with the
      // hidden rows.
      ...(dueByCgId
        ? []
        : [
            {
              id: "dragHandle",
              header: "",
              width: 32,
              align: "center",
              cell: () => <GripVertical className="h-4 w-4 text-muted-foreground" />,
            } satisfies Column<BoundRow>,
          ]),
      {
        id: "name",
        header: t(Namespace.PickupLocation, "groups.table.name"),
        key: "locationName",
        sortable: true,
        cell: ({ row }) => (
          <Hyperlink onOpen={() => openLocationCard(row.original)}>
            {row.original.locationName}
          </Hyperlink>
        ),
      },
      {
        id: "containerGroups",
        header: t(Namespace.PickupLocation, "groups.table.containerGroups"),
        align: "center",
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1 text-sm">
            <span>{row.original.cg.containerType?.name ?? "—"}</span>
            <span className="text-muted-foreground">{row.original.cg.numberOfContainers ?? 0}</span>
          </span>
        ),
      },
      {
        id: "schedule",
        header: t(Namespace.PickupLocation, "groups.table.schedule"),
        align: "center",
        cell: ({ row }) => scheduleLabel(t, row.original.hasSchedule),
      },
      {
        id: "jobTypes",
        header: t(Namespace.PickupLocation, "groups.table.jobType"),
        cell: ({ row }) =>
          row.original.jobTypes.length > 0 ? (
            <span className="flex flex-col items-start gap-1">
              {row.original.jobTypes.map((jobType) => (
                <Badge key={jobType} variant="secondary" className="font-normal">
                  {t(Namespace.Enum, jobType as TranslationKey)}
                </Badge>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "customer",
        header: t(Namespace.PickupLocation, "groups.table.customer"),
        key: "customerName",
        sortable: true,
        cell: ({ row }) => {
          const { customer, customerName } = row.original;
          if (!customer) return customerName;
          return <Hyperlink onOpen={() => openCustomerCard(customer)}>{customerName}</Hyperlink>;
        },
      },
      {
        id: "actions",
        header: "",
        width: 48,
        align: "center",
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full text-red-500 opacity-0 transition-opacity hover:bg-transparent hover:text-red-600 group-hover:opacity-100"
            title={t(Namespace.PickupLocation, "groups.table.unbind")}
            disabled={unbindingCgId !== null}
            onClick={(e) => {
              e.stopPropagation();
              handleUnbind(row.original.cg);
            }}
          >
            <IcCancel className="h-6 w-6" />
          </Button>
        ),
      },
    ],
    [t, openLocationCard, openCustomerCard, handleUnbind, unbindingCgId, dueByCgId],
  );

  // Same columns, in the same order, as the table above — minus the drag handle
  // and the row actions, which carry no data.
  const exportColumns = useMemo(
    (): Column<BoundRow>[] => [
      {
        id: "index",
        header: t(Namespace.PickupLocation, "groups.table.number"),
        accessor: (row) => row.index,
      },
      {
        id: "name",
        header: t(Namespace.PickupLocation, "groups.table.name"),
        accessor: (row) => row.locationName,
      },
      {
        id: "containerGroups",
        header: t(Namespace.PickupLocation, "groups.table.containerGroups"),
        accessor: (row) => `${row.cg.containerType?.name ?? "—"} ${row.cg.numberOfContainers ?? 0}`,
      },
      {
        id: "schedule",
        header: t(Namespace.PickupLocation, "groups.table.schedule"),
        accessor: (row) => scheduleLabel(t, row.hasSchedule),
      },
      {
        id: "jobTypes",
        header: t(Namespace.PickupLocation, "groups.table.jobType"),
        accessor: (row) =>
          row.jobTypes.length > 0
            ? row.jobTypes.map((jobType) => t(Namespace.Enum, jobType as TranslationKey)).join(", ")
            : "—",
      },
      {
        id: "customer",
        header: t(Namespace.PickupLocation, "groups.table.customer"),
        accessor: (row) => row.customerName,
      },
    ],
    [t],
  );

  // Exports exactly what the table shows: current order and active search.
  const handleDownloadExcel = useCallback(() => {
    try {
      exportToExcel({
        data: rows,
        columns: exportColumns,
        filename: generateFilename(`group_${group.name.trim().replace(/\s+/g, "_")}`),
        sheetName: toSheetName(group.name),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t(Namespace.PickupLocation, "groups.notifications.error"),
        description: String((error as Error)?.message ?? error),
      });
    }
  }, [rows, exportColumns, group.name, toast, t]);

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-4">
      {/* Header: name + responsible truck */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="truncate text-xl font-bold tracking-tight">{group.name}</h2>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={t(Namespace.PickupLocation, "groups.editGroup")}
            onClick={() => setEditOpen(true)}
          >
            <Pencil className="h-4 w-4 text-blue-600" />
          </Button>
        </div>

        {/* Read-only: the truck is changed via the edit modal, where only
            trucks compatible with the group's bindings are offered. */}
        <div className="flex shrink-0 items-center gap-2 text-sm">
          <span className="rounded-md border px-2 py-1.5 font-semibold">
            {assignedTruck?.plateNumber ?? t(Namespace.PickupLocation, "groups.noTruck")}
          </span>
          {assignedTruck?.truckType && (
            <>
              <span>
                {assignedTruck.truckType.manufacturer} {assignedTruck.truckType.model}
              </span>
              {!!assignedTruck.truckType.type && (
                <span>
                  {t(
                    Namespace.Enum,
                    `TRUCK_TYPE_${ConnectProto.TruckType[assignedTruck.truckType.type]}` as TranslationKey,
                  )}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Toolbar: search, the single-day filter centred, actions. Figma frame
          "Pickup location group / single day". */}
      {/* Both side tracks may shrink to nothing, so they always stay equal and
          the filter stays centred on the panel; the search field narrows
          instead. */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4">
        <div className="relative w-full max-w-60">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(Namespace.PickupLocation, "groups.searchLocation")}
            className="h-8 rounded-lg pl-8 text-sm"
          />
        </div>

        <ServiceDayFilterControls serviceDay={serviceDay} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 justify-self-end rounded-lg"
              aria-label={t(Namespace.PickupLocation, "groups.actions")}
            >
              <MoreHorizontal className="h-5 w-5 text-text-primary" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem className="gap-2" onClick={() => setBindOpen(true)}>
              <IcGroupOfLocations className="h-5 w-5 text-brand-blue" />
              <span>{t(Namespace.PickupLocation, "groups.addLocation")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* A group with no bound container groups would open the modal on an
                empty route, so it is offered only once there is something to run. */}
            <DropdownMenuItem
              className="gap-2"
              disabled={!hasBoundContainerGroups || isPreviewingRoute}
              onClick={() => handleCreateRouteFromGroup(false)}
            >
              <IcRoutes className="h-5 w-5 text-brand-blue" />
              <span>{t(Namespace.PickupLocation, "groups.createRouteFromGroup")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="gap-2"
              disabled={!hasBoundContainerGroups || isLoadingTemplate}
              onClick={() => handleCreateRouteFromGroup(true)}
            >
              <IcRoutes className="h-5 w-5 text-brand-blue" />
              <span>{t(Namespace.PickupLocation, "groups.createRouteFromTemplate")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="gap-2"
              disabled={rows.length === 0}
              onClick={handleDownloadExcel}
            >
              <IcExcel className="h-5 w-5 text-status-success" />
              <span>{t(Namespace.PickupLocation, "groups.downloadExcel")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats of the rows shown: with the day filter on, that day's. */}
      <div className="flex justify-center">
        <div className="flex h-8 items-center rounded-lg bg-slate-100 text-xs font-bold uppercase text-text-subtle">
          <span className="px-4">
            {t(Namespace.PickupLocation, "groups.containerGroupsCount", {
              count: stats.containerGroupsCount,
            })}
          </span>
          <Separator orientation="vertical" className="h-full bg-white" />
          <span className="px-4">
            {t(Namespace.PickupLocation, "groups.locationsCount", {
              count: stats.locationsCount,
            })}
          </span>
          <Separator orientation="vertical" className="h-full bg-white" />
          <span className="px-4">
            {t(Namespace.PickupLocation, "groups.containersCount", {
              count: stats.containersCount,
            })}
          </span>
          <Separator orientation="vertical" className="h-full bg-white" />
          <span className="px-4">
            {t(Namespace.PickupLocation, "groups.volumeCount", { count: stats.totalVolume })}
          </span>
        </div>
      </div>

      {/* Bound container groups table */}
      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable<BoundRow>
          columns={columns}
          data={rows}
          rowKey={(row) => String(row.cg.id)}
          rowClassName={() => "group"}
          loading={loading || serviceDay.loading}
          enableRowDrag={!search.trim() && !dueByCgId}
          dragColumnId="dragHandle"
          onRowDragEnd={handleRowDragEnd}
          empty={
            <div className="p-6 text-center text-sm text-muted-foreground">
              {t(
                Namespace.PickupLocation,
                // The day emptied the table, not the search box or an unbound group.
                dueByCgId && !search.trim() && (group.containerGroupLinks ?? []).length > 0
                  ? "groups.table.emptyForDay"
                  : "groups.table.empty",
              )}
            </div>
          }
        />
      </div>

      <EditGroupModal
        open={editOpen}
        onOpenChange={setEditOpen}
        group={group}
        trucks={trucks}
        onDeleted={onDeleted}
      />

      <BindLocationsModal
        open={bindOpen}
        onOpenChange={setBindOpen}
        groupId={group.id}
        ownerOrganizationId={group.ownerOrganizationId}
        defaultTruckId={group.defaultTruckId}
        trucks={trucks}
        locations={locations}
        boundJobTypesByCgId={linkJobTypesByCgId}
      />

      {pickupLocationCard}
    </div>
  );
};
